/**
 * PokerBuyInBoostPanel — admin controls for the "first N players get bonus
 * chips on buy-in" promo. Sits under the Happy Hour panel in Admin → Settings.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PokerBuyInBoostPanel() {
    const [tables, setTables] = useState([]);
    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({
        enabled: false, bonus_amount: 10, max_recipients: 6,
        scope: 'all', expires_hours: 24,
    });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const [tbl, boost] = await Promise.all([
                axios.get(`${API}/poker-room/tables`, { withCredentials: true }),
                axios.get(`${API}/poker-room/buyin-boost`, { withCredentials: true }),
            ]);
            setTables(tbl.data.tables || []);
            setCfg(boost.data);
            setForm({
                enabled: boost.data.enabled,
                bonus_amount: boost.data.bonus_amount,
                max_recipients: boost.data.max_recipients,
                scope: boost.data.scope,
                expires_hours: 24,
            });
        } catch { /* silent */ }
    }, []);

    useEffect(() => { load(); const t = setInterval(load, 15_000); return () => clearInterval(t); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            const res = await axios.put(`${API}/poker-room/admin/buyin-boost`, form,
                                        { withCredentials: true });
            toast.success(form.enabled
                ? `Buy-In Boost ON · +$${res.data.bonus_amount} for first ${res.data.max_recipients} players`
                : 'Buy-In Boost turned OFF');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        } finally { setSaving(false); }
    };

    if (!cfg) return null;
    return (
        <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0f0f14] to-[#050508] p-4 md:p-5"
             data-testid="poker-buyin-boost-panel">
            <div className="flex items-center gap-2 mb-2">
                <Gift className="w-5 h-5 text-pink-400" />
                <h3 className="text-lg font-bold text-white">Poker · Buy-In Boost</h3>
            </div>
            <p className="text-xs text-white/50 mb-4">
                Free chips added on top of the first N buy-ins. Great weekend / holiday promo. Bonus lives on the table and cashes back into the wallet on leave, just like the regular stack.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <label className="flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg px-3 py-2 cursor-pointer">
                    <input type="checkbox" checked={form.enabled}
                           onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                           data-testid="boost-enabled" />
                    <span className="text-sm">Promo ACTIVE</span>
                </label>
                <select value={form.scope}
                        onChange={(e) => setForm({ ...form, scope: e.target.value })}
                        className="bg-black/60 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
                        data-testid="boost-scope">
                    <option value="all">All Tables</option>
                    {tables.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Bonus $ per player</label>
                    <input type="number" step="1" min="0" max="1000"
                           value={form.bonus_amount}
                           onChange={(e) => setForm({ ...form, bonus_amount: Number(e.target.value) })}
                           className="w-full bg-black/60 border border-white/20 rounded-lg px-3 py-2 text-white font-mono"
                           data-testid="boost-amount" />
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Max Recipients</label>
                    <input type="number" step="1" min="1" max="200"
                           value={form.max_recipients}
                           onChange={(e) => setForm({ ...form, max_recipients: Number(e.target.value) })}
                           className="w-full bg-black/60 border border-white/20 rounded-lg px-3 py-2 text-white font-mono"
                           data-testid="boost-max" />
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-wider text-white/50 mb-1">Expires in (hours, 0 = no expiry)</label>
                    <input type="number" step="1" min="0" max="720"
                           value={form.expires_hours}
                           onChange={(e) => setForm({ ...form, expires_hours: Number(e.target.value) })}
                           className="w-full bg-black/60 border border-white/20 rounded-lg px-3 py-2 text-white font-mono"
                           data-testid="boost-expires" />
                </div>
            </div>

            <button onClick={save} disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-pink-500 hover:bg-pink-400 text-black rounded-lg text-sm font-black disabled:opacity-40"
                    data-testid="boost-save">
                <Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save Boost Config'}
            </button>

            <div className="mt-4 rounded-lg border border-white/10 bg-black/30 p-3 text-xs">
                <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1">Current promo status</div>
                {cfg.enabled ? (
                    <div className="text-emerald-300">
                        ACTIVE · +${cfg.bonus_amount} bonus · <b>{cfg.remaining}</b> of {cfg.max_recipients} slots left ·
                        scope: {cfg.scope}
                        {cfg.expires_at ? ` · expires ${new Date(cfg.expires_at * 1000).toLocaleString(undefined, { timeZone: 'America/Chicago' })}` : ''}
                    </div>
                ) : (
                    <div className="text-white/50">Not active.</div>
                )}
            </div>
        </div>
    );
}
